import { Client } from "@/Common/Api/Client";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { TextAreaAutoSize } from "@/Common/Components/Inputs/TextAreaAutoSize";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ClickableTextFooter } from "./ClickableTextFooter";

import "./ClickableTextEditor.css";

interface LoadRawTextResponse {
    TabContent: string;
    Id: string;
    TabOrder: number;
}

interface ClickableTextEditorProps {
    context: string;
    editMode: boolean;
    onCloseCallback?: () => void;
    tabName: string;
    employeeId?: string;
    IsInAdminMode?: boolean;
    enumAdminMode?: number;
    isFromAnotherUser?: boolean;
    isGroupMode?: boolean;
    employeeGroupId?: string;
    clickableContent?: string;
    isAdminCreateMode?: boolean;
    languageCode?: string;
    isHorizontalAligned?: boolean;
    leftAlign?: boolean;
    className?: string;
}

enum ClickableTextEditStatus {
    Success = 0,
    TabNameCollision,
    Failed
}

class ClickableTextEditorState {
    constructor(fields?: Partial<ClickableTextEditorState>) {
        (Object as any).assign(this, fields);
    }

    public tabItemId: string;
    public tabName: string;
    public tabContextContentReloadId: number;
    public tabContextContent: string;
    public tabContextContentItems: string[];

    public isTitleCollisionErrorVisible: boolean;
    public isSaving: boolean;
    public saveButtonStringKey: string;
    public nameCss: string;
}

class ClickableTextEditor extends BaseReactComponent<ClickableTextEditorProps, ClickableTextEditorState> {
    constructor(props: ClickableTextEditorProps) {
        super(props);

        const initialState = new ClickableTextEditorState();
        initialState.isSaving = false;
        initialState.tabName = this.props.tabName;
        initialState.tabContextContentReloadId = 0;
        initialState.tabContextContent = "";
        initialState.tabContextContentItems = [];
        initialState.saveButtonStringKey = "SaveLabel";
        initialState.isTitleCollisionErrorVisible = false;
        initialState.nameCss = this.getCssForNameField(this.props.tabName);
        this.state = initialState;
    }

    public render(): JSX.Element {
        let style: React.CSSProperties = {};
        if (this.props.isHorizontalAligned)
            style.opacity = 0;

        if (this.props.leftAlign)
            style.left = 0;
        const savebtnDisabled = IsNullOrWhiteSpace(this.state.tabName) || this.state.isSaving;
        const saveDeletebtnDisabledForAdminMode = this.props.enumAdminMode == 1;

        const cssForNameField = `form-control ${this.state.nameCss}`;
        return (
            <div className={`cl-text-editor-container ${this.props.className ?? ""}`} onClick={e => e.stopPropagation()}>
                <div className="limiter">
                    <div className="flex-container">
                        <div className="editor">
                            {this.state.isTitleCollisionErrorVisible === true && (
                                <div className="from-group has-error">
                                    <label><span id="blinky" className="red-text">{cacheStore.GetString("TabNameExistWarningLabel")}</span></label>
                                    <input type="text" value={this.state.tabName} onChange={(e: any) => this.handleTabNameChange(e.target.value)} className="form-control" />
                                </div>
                            )}
                            {this.state.isTitleCollisionErrorVisible === false && (
                                <div>
                                    <label>{cacheStore.GetString("NameDLabel")}</label>
                                    <input type="text" value={this.state.tabName} onChange={(e: any) => this.handleTabNameChange(e.target.value)} className={cssForNameField} data-cy="clickableTextEditor-name" />
                                </div>
                            )}

                            <div>
                                <label>{cacheStore.GetString("BodyDLabel")}</label>
                                <TextAreaAutoSize
                                    id={`clickableTextEditor_${this.state.tabContextContentReloadId}`}
                                    value={this.state.tabContextContent}
                                    onTextChangeCallback={value => this.updateTabContextContent(value)}
                                    nowrap={true}
                                    minHeight="410px"
                                    dataCy="clickableTextEditor-body"
                                />
                            </div>
                        </div>
                        <div className="preview">
                            <div className="preview-container">
                                <div className="overflow-control" style={{ whiteSpace: "pre" }}>
                                    <ul className="clickable-text-list">
                                        {this.state.tabContextContentItems.map((s, idx) => (
                                            <li key={idx + "___cli"} style={{ flexWrap: "wrap" }}>
                                                <div className="flex-container" style={{ justifyContent: "flex-start", display: "flex" }}>
                                                    <div style={{ width: "10px" }}>
                                                        <span className="dot" />
                                                    </div>
                                                    <div style={{ width: "10px" }}>
                                                        {this.splitClickableTextItems(s).map((sp, idx2) => (
                                                            <a key={"individual_link_" + idx2} style={{ marginRight: "5px", lineHeight: "15px", minHeight: "35px", marginBottom: "6px" }}>
                                                                <span>{sp}</span>
                                                            </a>
                                                        ))}
                                                    </div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="footer" data-cy="clickableTextEditor-footer">
                    <ClickableTextFooter
                        inputElementId={`clickableTextEditor_${this.state.tabContextContentReloadId}`}
                        isFromClickableTextEditor={true}
                    />
                    {this.props.editMode && (
                        <div className="text-right">
                            <button className="btn btn-sm btn-default push-right-5" onClick={() => this.closeForm()} disabled={this.state.isSaving}><span>{cacheStore.GetString("CancelLabel")}</span></button>
                            <button className="btn btn-sm btn-danger push-right-5" onClick={() => this.openDeleteFrom()} disabled={this.state.isSaving || saveDeletebtnDisabledForAdminMode || this.props.isFromAnotherUser} data-cy="clickableTextEditor-delete"><span>{cacheStore.GetString("DeleteLabel")}</span></button>
                            <button className="btn btn-sm btn-primary push-right" onClick={() => this.saveClickableText(false)} disabled={savebtnDisabled || saveDeletebtnDisabledForAdminMode || this.props.isFromAnotherUser} data-cy="clickableTextEditor-save"><span>{cacheStore.GetString(this.state.saveButtonStringKey)}</span></button>
                        </div>
                    )}
                    {this.props.editMode === false && (
                        <div className="text-right">
                            <button className="btn btn-sm btn-default push-right-5" onClick={() => this.closeForm()} disabled={this.state.isSaving}><span>{cacheStore.GetString("CancelLabel")}</span></button>
                            <button className="btn btn-sm btn-primary push-right" onClick={() => this.saveClickableText(true)} disabled={savebtnDisabled} data-cy="clickableTextEditor-save"><span>{cacheStore.GetString(this.state.saveButtonStringKey)}</span></button>
                        </div>
                    )}

                </div>

            </div>
        );
    }

    public componentDidMount(): void {
        this.setOverlay("ClickableTextEditorOverlay", { transparent: false });

        if (this.props.editMode)
            this.loadTab();
    }

    private handleTabNameChange(tabName: string): void {
        this.mutateState(s => {
            s.tabName = tabName;
            s.nameCss = this.getCssForNameField(tabName);
        });
    }

    private getCssForNameField(tabName: string): string {
        const isValidName = !IsNullOrWhiteSpace(tabName);
        return isValidName ? "valid" : "invalid";
    }

    private loadTab(): void {
        if (this.props.isAdminCreateMode)
            this.mutateState(s => {
                s.tabContextContentReloadId = sessionStore.newId();
                s.tabContextContent = this.props.clickableContent;
            });
        else {
            Client.Post(
                ServerOperations.Server.Employee_GetRawClickableText,
                {
                    EmployeeId: ((this.props.employeeId != null) ? this.props.employeeId : sessionStore.individualContactId),
                    Context: this.props.context,
                    TabName: this.props.tabName,
                    TabValue: this.props.clickableContent,
                    LanguageCode: IsNullOrWhiteSpace(this.props.languageCode) ? sessionStore.userLanguageCode : this.props.languageCode,
                    EmployeeGroupId: this.props.employeeGroupId,
                    IsGroupMode: this.props.isGroupMode
                },
                (response: LoadRawTextResponse) => {
                    this.mutateState(s => {
                        s.tabContextContentReloadId = sessionStore.newId();
                        s.tabContextContent = response.TabContent;
                        s.tabContextContentItems = this.parseClickableContent(response.TabContent);
                        s.tabItemId = response.Id;
                    });
                }
            );
        }
    }

    private updateTabContextContent(value: string): void {
        this.mutateState(s => {
            s.tabContextContent = value;
            s.tabContextContentItems = this.parseClickableContent(value);
        });
    }

    private parseClickableContent(str: string): string[] {
        if (IsNullOrWhiteSpace(str))
            return [];

        const pattern = new RegExp("/\\*(((?!\\*/)[\\S\\s])*)\\*/", "g"); // Get All the Items in Between this encapsulation /**/. Exemple: /* TextHere */

        const matchRanges: string[] = [];
        let result: RegExpExecArray = null;
        while ((result = pattern.exec(str)) !== null) {
            matchRanges.push(result[1]);
        }

        return matchRanges.filter(x => !IsNullOrWhiteSpace(x));
    }

    private splitClickableTextItems(txt: string): string[] {
        if (IsNullOrWhiteSpace(txt))
            return [];

        return txt.split(";").filter(s => !IsNullOrWhiteSpace(s));
    }

    private saveClickableText(isCreate: boolean): void {
        const data = {
            EmployeeId: ((this.props.employeeId != null) ? this.props.employeeId : sessionStore.individualContactId),
            Context: this.props.context,
            TabName: this.state.tabName,
            TabValue: this.state.tabContextContent,
            Id: isCreate ? null : this.state.tabItemId,
            LanguageCode: IsNullOrWhiteSpace(this.props.languageCode) ? sessionStore.userLanguageCode : this.props.languageCode,
            EmployeeGroupId: this.props.employeeGroupId,
            IsGroupMode: this.props.isGroupMode
        };

        if (isCreate)
            Client.Post(ServerOperations.Server.Employee_CreateClickableText, data, response => this.onCreateSuccess(response));
        else
            Client.Post(ServerOperations.Server.Employee_EditClickableText, data, response => this.onEditComplete(response));

        const s = new ClickableTextEditorState();
        s.isSaving = true;
        this.setState(s);

        if (this.props.IsInAdminMode)
            sessionStore.dispatch(LocalOperations.AdministrationUsers.CloseRefreshClickableText);

        if (this.props.isGroupMode)
            sessionStore.dispatch(LocalOperations.AdministrationGroup.CloseRefreshClickableText);
    }

    private onEditComplete(response: { Status: ClickableTextEditStatus }): void {
        switch (response.Status) {
            case ClickableTextEditStatus.Success:
                if (typeof this.props.onCloseCallback === "function") {
                    this.props.onCloseCallback();
                    window.setTimeout(() => sessionStore.dispatchMain(LocalOperations.MainComponentViewOperations.RefreshTabs), 20);
                }
                break;

            case ClickableTextEditStatus.TabNameCollision:
                this.mutateState(s => {
                    s.isTitleCollisionErrorVisible = true;
                    s.isSaving = false;
                });

                this.blinkText();
                break;

            default:
                this.mutateState(s => s.isSaving = false);
                console.error("edit failed? debug");
                break;
        }
    }

    private openDeleteFrom(): void {
        const content = [`${cacheStore.GetString("DeleteClickableTextLabel1")}${this.props.context} - ${this.props.tabName}?`, cacheStore.GetString("DeleteClickableTextLabel2")];
        sessionStore.showPopup({
            title: cacheStore.GetString("DeleteClickableText"),
            content: content,
            showCancel: true,

            okLabel: cacheStore.GetString("ContinueLabel"),
            okAction: () => this.deleteFrom()
        });
    }

    private deleteFrom(): void {
        const client = new Client();
        client.Post(
            apiSettings.buildApiPath(),
            ServerOperations.Server.Employee_DeletelickableTextTab,
            { Id: this.state.tabItemId },
            () => this.onTabDeleted(),
            e => this.onTabDeleteError(e)
        );

        if (this.props.IsInAdminMode)
            sessionStore.dispatch(LocalOperations.AdministrationUsers.CloseRefreshClickableText);

        if (this.props.isGroupMode)
            sessionStore.dispatch(LocalOperations.AdministrationGroup.CloseRefreshClickableText);
    }

    private onTabDeleted(): void {
        sessionStore.dispatchMain(LocalOperations.MainComponentViewOperations.RefreshTabs);
    }

    private onTabDeleteError(e: any): void {
        console.log(e);
    }

    private onCreateSuccess(response: { Status: ClickableTextEditStatus }): void {
        switch (response.Status) {
            case ClickableTextEditStatus.Success:
                this.mutateState(s => s.saveButtonStringKey = "DoneLabel");

                window.setTimeout(
                    () => {
                        if (typeof this.props.onCloseCallback === "function") {
                            sessionStore.dispatchMain(LocalOperations.MainComponentViewOperations.RefreshTabs);
                            this.props.onCloseCallback();
                        }
                    },
                    1000
                );
                break;

            case ClickableTextEditStatus.TabNameCollision:
                this.mutateState(s => {
                    s.isSaving = false;
                    s.isTitleCollisionErrorVisible = true;
                });

                this.blinkText();
                break;

            default:
                console.error("save failed. debug");

                this.mutateState(s => s.isSaving = false);
                break;
        }
    }

    private closeForm(): void {
        sessionStore.dispatchMain(LocalOperations.MainComponentViewOperations.RefreshTabs);
        this.props.onCloseCallback && this.props.onCloseCallback();
    }

    private blinkText(): void {
        const ref = $("#blinky");

        let show = false;
        const intervalKiller = window.setInterval(() => {
            ref.css("visibility", show ? "visible" : "hidden");
            show = !show;
        }, 200);

        window.setTimeout(() => {
            clearInterval(intervalKiller);
            ref.css("visibility", "visible");
        }, 801);

        window.setTimeout(() => this.mutateState(s => s.isTitleCollisionErrorVisible = false), 4000);
    }
}

export { ClickableTextEditor };
